import { useCallback, useEffect, useRef } from 'react';
import {
  getAccountPaymentOrder,
  getAccountPaySession,
  type AccountPaySession,
  type AccountPaymentOrder,
} from '../api';

const PAYMENT_ORDER_POLL_INTERVAL_MS = 2000;

interface UsePaymentOrderPollingOptions {
  open: boolean;
  paySession: AccountPaySession | null;
  onPaid: (order?: AccountPaymentOrder) => void | Promise<void>;
  onError?: (error: unknown) => void;
}

type PaymentOrderStatus = 'PENDING' | 'PAID' | 'FAILED';

const PENDING_ORDER_STATUS_SET = new Set(['PENDING', 'PREPAY']);
const PAID_ORDER_STATUS = 'PAID';

function createPaymentOrderError(message: string) {
  return new Error(message || '支付状态异常，请稍后重试');
}

function resolvePaymentOrderStatus(status?: string | null): PaymentOrderStatus {
  const normalizedStatus = (status || '').toUpperCase();
  if (PENDING_ORDER_STATUS_SET.has(normalizedStatus)) {
    return 'PENDING';
  }
  if (normalizedStatus === PAID_ORDER_STATUS) {
    return 'PAID';
  }
  return 'FAILED';
}

function isPaidOrder(order?: AccountPaymentOrder | null) {
  return resolvePaymentOrderStatus(order?.status) === 'PAID';
}

function isPendingOrder(order?: AccountPaymentOrder | null) {
  return resolvePaymentOrderStatus(order?.status) === 'PENDING';
}

export function usePaymentOrderPolling({
  open,
  paySession,
  onPaid,
  onError,
}: UsePaymentOrderPollingOptions) {
  const timerRef = useRef<number | null>(null);
  const orderNoRef = useRef('');
  const handledOrderNoRef = useRef('');
  const pollingRef = useRef(false);
  const pollingEnabledRef = useRef(false);
  const activeSessionTokenRef = useRef('');
  const terminalHandledRef = useRef(false);

  const clearPollingTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finishPolling = useCallback(() => {
    pollingEnabledRef.current = false;
    clearPollingTimer();
  }, [clearPollingTimer]);

  const handlePaid = useCallback(async (order?: AccountPaymentOrder) => {
    if (terminalHandledRef.current) {
      return;
    }
    terminalHandledRef.current = true;
    finishPolling();
    await onPaid(order);
  }, [finishPolling, onPaid]);

  const handlePollingError = useCallback((error: unknown) => {
    if (terminalHandledRef.current) {
      return;
    }
    terminalHandledRef.current = true;
    finishPolling();
    onError?.(error);
  }, [finishPolling, onError]);

  const pollOrder = useCallback(async () => {
    if (!paySession || pollingRef.current || terminalHandledRef.current) {
      return;
    }

    pollingRef.current = true;
    try {
      let order: AccountPaymentOrder | null = null;
      let knownOrderNo = orderNoRef.current || paySession.paymentOrderNo || '';

      if (!knownOrderNo) {
        // H5 扫码页创建预支付单后，支付会话会带回本次订单号；拿到订单号后再查订单详情。
        const latestSession = await getAccountPaySession(paySession.paySessionToken);
        if (latestSession?.status) {
          const sessionStatus = resolvePaymentOrderStatus(latestSession.status);
          if (sessionStatus === 'PAID') {
            await handlePaid();
            return;
          }
          if (sessionStatus === 'FAILED') {
            handlePollingError(createPaymentOrderError(`支付状态异常: ${latestSession.status}`));
            return;
          }
        }
        knownOrderNo = latestSession?.paymentOrderNo || '';
        orderNoRef.current = knownOrderNo;
      }

      if (knownOrderNo) {
        order = await getAccountPaymentOrder(knownOrderNo);
        orderNoRef.current = order.paymentOrderNo || knownOrderNo;
      }

      if (
        !pollingEnabledRef.current
        || activeSessionTokenRef.current !== paySession.paySessionToken
      ) {
        return;
      }

      if (isPendingOrder(order) || !order) {
        return;
      }

      if (isPaidOrder(order)) {
        if (order.paymentOrderNo && handledOrderNoRef.current === order.paymentOrderNo) {
          return;
        }
        handledOrderNoRef.current = order.paymentOrderNo || paySession.paySessionToken;
        await handlePaid(order);
        return;
      }

      handlePollingError(createPaymentOrderError(`支付状态异常: ${order.status || 'UNKNOWN'}`));
    } catch (error) {
      handlePollingError(error);
    } finally {
      pollingRef.current = false;
    }
  }, [handlePaid, handlePollingError, paySession]);

  useEffect(() => {
    clearPollingTimer();
    pollingEnabledRef.current = false;
    activeSessionTokenRef.current = '';
    orderNoRef.current = '';
    handledOrderNoRef.current = '';
    terminalHandledRef.current = false;

    if (!open || !paySession?.paySessionToken) {
      return undefined;
    }

    pollingEnabledRef.current = true;
    activeSessionTokenRef.current = paySession.paySessionToken;
    void pollOrder();
    timerRef.current = window.setInterval(() => {
      void pollOrder();
    }, PAYMENT_ORDER_POLL_INTERVAL_MS);

    return () => {
      pollingEnabledRef.current = false;
      activeSessionTokenRef.current = '';
      clearPollingTimer();
    };
  }, [clearPollingTimer, open, paySession?.paySessionToken, pollOrder]);
}
